import { Edge, Node, NodeProps } from "reactflow";
import { useAtomValue, useSetAtom } from "jotai";
import {
  nodesAtom,
  edgesAtom,
  nodeAtomFamily,
  takeSnapshotAtom,
  lastBranchingPathPositionAtomFamily,
  MARKER,
  CHILD_NODE_HEIGHT,
  GAP,
  saveCanvasAsyncCommand,
} from "@zapier/canvas-state";

import { uuid } from "../utils/uuid";

function calculateBranchingPathY(lastBranchingPathY: number, index: number) {
  return (
    lastBranchingPathY +
    CHILD_NODE_HEIGHT +
    GAP * (index + 1) +
    CHILD_NODE_HEIGHT * index
  );
}

// this hook implements the logic for adding individual branching paths
export function useAddPaths(
  pathNodeId: NodeProps["id"],
  numOfPaths: number,
  shouldAutoSelect: boolean = true,
) {
  const setNodes = useSetAtom(nodesAtom);
  const setEdges = useSetAtom(edgesAtom);
  const saveToBackend = useSetAtom(saveCanvasAsyncCommand);
  const takeSnapshot = useSetAtom(takeSnapshotAtom);

  let updatedNewNodes = useAtomValue(nodesAtom);
  let updatedNewEdges = useAtomValue(edgesAtom);
  const pathNode = useAtomValue(nodeAtomFamily(pathNodeId));
  const lastBranchingPathPosition = useAtomValue(
    lastBranchingPathPositionAtomFamily(pathNodeId),
  );

  return async () => {
    if (!pathNodeId) {
      throw new Error("Need a path node id to add a branching path");
    }

    if (!pathNode) {
      return;
    }

    takeSnapshot();

    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];

    for (let i = 0; i < numOfPaths; i++) {
      const branchingPathId = uuid();

      const branchingPathY = calculateBranchingPathY(
        lastBranchingPathPosition.y,
        i,
      );

      const branchingPath: Node = {
        id: branchingPathId,
        position: {
          x: lastBranchingPathPosition.x,
          y: branchingPathY,
        },
        type: "default",
        selected: shouldAutoSelect ? i === 0 : false,
        data: {
          label: "",
        },
      };

      newNodes.push(branchingPath);

      const sourceToBranchingPathEdge: Edge = {
        id: uuid(),
        source: pathNode.id,
        target: branchingPathId,
        sourceHandle: "right",
        targetHandle: "left",
        markerEnd: MARKER,
      };

      newEdges.push(sourceToBranchingPathEdge);
    }

    setNodes((nodes) => {
      const deselectedNodes: Node[] = nodes.map((node) => ({
        ...node,
        selected: shouldAutoSelect ? false : node.selected,
      }));
      updatedNewNodes = deselectedNodes.concat(newNodes);
      return updatedNewNodes;
    });

    setEdges((edges) => {
      updatedNewEdges = edges.concat(newEdges);
      return updatedNewEdges;
    });

    saveToBackend({
      nodes: updatedNewNodes,
      edges: updatedNewEdges,
    });
  };
}
